import { useEffect, useState } from "react";
import { Moon, Sun, User, LogOut, Settings, BarChart3, Home, ChevronDown, PieChart, Briefcase } from "lucide-react";
import { useTheme } from "../../zustand/ThemeStore";
import { useLocation, useNavigate } from "react-router-dom";
import { useUserStore } from "../../zustand/UserStore";
import { getNavigationPermissions } from "../../hooks/useAuthorization";
import strideLogo from "../../assets/Final Logo Used in White Label.png";
import ExtensionBanner from "./ExtensionBanner";

export default function Header({ children }: { children: React.ReactNode }) {
    const isDarkMode = useTheme((set) => set.theme);
    const setIsDarkMode = useTheme((set) => set.setTheme);
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const [isSelected, setSelected] = useState(0);
    const nav = useNavigate();
    const location = useLocation();
    const userData = useUserStore((set)=>set.user);

    // Get navigation permissions based on user role
    const navPermissions = userData ? getNavigationPermissions(userData.role as any) : {
        showMainDashboard: false,
        showUserDashboard: false,
        showAnalysisDashboard: false,
        showSettings: false,
        defaultRoute: '/user'
    };

    useEffect(()=> {
        if(location.pathname=='/main'){
            setSelected(0);
        }
        else if (location.pathname=='/analysis' || location.pathname.startsWith('/analysis/')) {
            setSelected(1);
        }
        else if (location.pathname.startsWith('/user/')) {
            setSelected(2);
        }
        else if (location.pathname=='/jobs') {
            setSelected(3);
        }
        else if (location.pathname=='/settings') {
            setSelected(4);
        }
        else {
            setSelected(-1);
        }
        
    },[location.pathname]);

    const handleLogout = () => {
        // Clear authentication token
        localStorage.removeItem('token');
        
        // Clear user from store
        useUserStore.getState().clearUser();
        
        console.log("Logging out...");
        setIsProfileOpen(false);
        nav('/auth');
    };

    const navigationItems = [
        {
            id: 0,
            label: "Overview",
            icon: Home,
            route: "/main",
            show: navPermissions.showMainDashboard
        },
        {
            id: 1,
            label: "Analysis",
            icon: BarChart3,
            route: "/analysis",
            show: navPermissions.showAnalysisDashboard || navPermissions.showUserDashboard
        },
        {
            id: 2,
            label: "My Dashboard",
            icon: PieChart,
            route: userData?.uid ? `/user/${userData.uid}` : "/user",
            show: navPermissions.showUserDashboard
        },
        {
            id: 3,
            label: "Logs",
            icon: Briefcase,
            route: "/jobs",
            show: true // Jobs should be visible to all authenticated users
        },
        {
            id: 4,
            label: "Settings",
            icon: Settings,
            route: "/settings",
            show: navPermissions.showSettings
        }
    ].filter(item => item.show);

    return (
        <div className={`min-h-screen transition-colors duration-300 ${isDarkMode ? 'bg-black' : 'bg-gray-100'}`}>
            {/* Extension Launch Banner */}
            <ExtensionBanner />
            
            <header className={`sticky top-0 z-50 shadow-lg backdrop-blur-2xl ${isDarkMode ? 'bg-[#0a1414] border-[#1a2424]' : 'bg-white/80 border-[#e0e0e0]'} border-b`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex justify-between items-center">
                        {/* Logo Section */}
                        <div className={`flex items-center gap-3 ${isDarkMode ? "bg-white": ""} h-14 rounded-lg`}>
                            <img 
                                src={strideLogo} 
                                alt="Stride Logo" 
                                className="w-32 h-auto"
                            />
                        </div>

                        {/* Navigation Menu */}
                        <nav className="hidden md:flex items-center gap-1">
                            {navigationItems.map((item) => {
                                const IconComponent = item.icon;
                                return (
                                    <button
                                        key={item.id}
                                        onClick={() => {
                                            setSelected(item.id);
                                            nav(item.route);
                                        }}
                                        className={`px-4 py-2 rounded-lg transition-all flex items-center gap-2 ${
                                            isSelected === item.id
                                                ? isDarkMode
                                                    ? 'bg-[#1a9f6b] text-black'
                                                    : 'bg-[#1a9f6b] text-white'
                                                : isDarkMode
                                                    ? 'bg-[#0f1919] text-white'
                                                    : 'bg-[#e0e0e0] text-[#053333]'
                                        }`}
                                    >
                                        <IconComponent className="w-4 h-4" />
                                        {item.label}
                                    </button>
                                );
                            })}
                        </nav>

                        {/* Right Section - Theme Toggle & Profile */}
                        <div className="flex items-center gap-3">
                            {/* Theme Toggle */}
                            <button
                                onClick={() => setIsDarkMode(!isDarkMode)}
                                className={`p-2.5 rounded-full transition-all border ${isDarkMode ? 'bg-[#050a0a] text-white border-[#1a2424]' : 'bg-[#f5f5f5] text-[#053333] border-[#e0e0e0]'}`}
                            >
                                {isDarkMode ? <Sun className="w-5 h-5 text-yellow-400" /> : <Moon className="w-5 h-5 text-[#1a9f6b]" />}
                            </button>

                            {/* Profile Dropdown */}
                            <div className="relative">
                                <button
                                    onClick={() => setIsProfileOpen(!isProfileOpen)}
                                    className={`flex items-center gap-2 p-2 rounded-full transition-all border ${isDarkMode ? 'bg-[#050a0a] text-white border-[#1a2424]' : 'bg-[#f5f5f5] text-[#053333] border-[#e0e0e0]'}`}
                                >
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-[#1a9f6b] text-black' : 'bg-[#17965f] text-[#053333]'}`}>
                                        <User className="w-4 h-4" />
                                    </div>
                                    <ChevronDown className={`w-4 h-4 transition-transform ${isProfileOpen ? 'rotate-180' : ''}`} />
                                </button>

                                {/* Dropdown Menu */}
                                {isProfileOpen && (
                                    <div className={`absolute right-0 mt-2 w-48 rounded-lg shadow-lg border ${isDarkMode ? 'bg-[#0a1414] border-[#1a2424]' : 'bg-white border-[#e0e0e0]'}`}>
                                        <div className={`px-4 py-3 border-b ${isDarkMode ? 'border-[#1a2424]' : 'border-[#e0e0e0]'}`}>
                                            <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-[#053333]'}`}>
                                                {userData?.name}
                                            </p>
                                            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-[#373737]'}`}>
                                                {userData?.email}
                                            </p>
                                            {userData?.role && (
                                              <span
                                                className={`inline-block mt-2 px-2 py-0.5 rounded-full text-xs font-semibold tracking-wide
                                                  ${isDarkMode
                                                    ? 'bg-[#1a9f6b]/10 text-[#1a9f6b] border border-[#1a9f6b]/40'
                                                    : 'bg-[#e6faf4] text-[#148856] border border-[#b2e5d3]'}
                                                `}
                                              >
                                                {userData.role}
                                              </span>
                                            )}
                                        </div>
                                        <div className="py-1">
                                            <button
                                                onClick={handleLogout}
                                                className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 transition-colors ${isDarkMode ? 'text-red-400 hover:bg-[#1a2424]' : 'text-red-600 hover:bg-[#e6faf4]'}`}
                                            >
                                                <LogOut className="w-4 h-4" />
                                                Sign Out
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Mobile Navigation */}
                    <nav className="md:hidden mt-4 flex gap-2 overflow-x-auto pb-2">
                        {navigationItems.map((item) => {
                            const IconComponent = item.icon;
                            return (
                                <button
                                    key={`mobile-${item.id}`}
                                    onClick={() => nav(item.route)}
                                    className={`flex-shrink-0 px-3 py-2 rounded-lg transition-all flex items-center gap-2 text-sm ${
                                        isDarkMode ? 'bg-[#0f1919] text-white' : 'bg-[#e0e0e0] text-[#053333]'
                                    }`}
                                >
                                    <IconComponent className="w-4 h-4" />
                                    {item.label}
                                </button>
                            );
                        })}
                    </nav>
                </div>

                {/* Backdrop for dropdown */}
                {isProfileOpen && (
                    <div 
                        className="fixed inset-0 z-40" 
                        onClick={() => setIsProfileOpen(false)}
                    />
                )}
            </header>
            {children}
        </div>
    );
}